/**
 * Skipper Field Guide
 *
 * Drives sections/skipper-field-guide.liquid: which guides are ticked, whether we are
 * picking or reading, the jump bar and the scroll animations.
 *
 * All content is rendered by Liquid up front (every guide body is in the DOM, hidden);
 * this only ever toggles visibility and chrome. Selection lives in three places, in
 * priority order:
 *
 *   1. ?guides=hand-wash,dish-liquid  — the source of truth, so a guide set is
 *      shareable, bookmarkable and survives the back button
 *   2. localStorage                   — remembers the last selection for a return visit
 *   3. nothing                        — the picker, with nothing ticked
 *
 * A #howto-hand-wash link (each guide's own id) also opens straight to that guide:
 * on its own, or added to a ?guides= set that comes with it. It's how a product page,
 * email or search result links to one guide without a URL of its own.
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'skipper:field-guide:selection';
  var PARAM = 'guides';
  var HASH_PREFIX = '#howto-';

  /* localStorage throws in private windows and when site data is blocked, and the page
     has to work either way, so every access is guarded. */
  function readStored() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? raw.split(',').filter(Boolean) : [];
    } catch (error) {
      return [];
    }
  }

  function writeStored(keys) {
    try {
      if (keys.length) {
        window.localStorage.setItem(STORAGE_KEY, keys.join(','));
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      /* Nothing to do — the selection simply will not be remembered. */
    }
  }

  function readParam() {
    var value = new URLSearchParams(window.location.search).get(PARAM);
    return value ? value.split(',').map(function (key) { return key.trim(); }).filter(Boolean) : [];
  }

  /* The guide key in a #howto-<key> deep link, or null. */
  function readHash() {
    var hash = window.location.hash;
    if (hash.indexOf(HASH_PREFIX) !== 0) return null;

    var key;
    try {
      key = decodeURIComponent(hash.slice(HASH_PREFIX.length));
    } catch (error) {
      return null;
    }

    return key.trim().toLowerCase() || null;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function parseTimestamp(value) {
    if (value == null) return null;

    var text = String(value).trim();
    if (!text) return null;

    if (text.indexOf(':') === -1) {
      var seconds = parseFloat(text);
      return isFinite(seconds) ? seconds : null;
    }

    var parts = text.split(':');
    var total = 0;

    for (var i = 0; i < parts.length; i += 1) {
      var part = parseFloat(parts[i]);
      if (!isFinite(part)) return null;
      total = (i === 0) ? part : total * 60 + part;
    }

    return total;
  }

  function FieldGuide(root) {
    this.root = root;
    this.selected = [];
    this.observers = [];
    this.listeners = [];
    this.videoSequences = [];

    this.panes = Array.prototype.slice.call(root.querySelectorAll('[data-fg-pane]'));
    this.actions = root.querySelector('.fg-actions');
    this.ctaButton = root.querySelector('[data-fg-submit]');
    this.ctaLabel = root.querySelector('[data-fg-cta-label]');
    this.status = root.querySelector('[data-fg-status]');
    this.jump = root.querySelector('[data-fg-jump]');
    this.pills = root.querySelector('[data-fg-jump-pills]');
    this.progress = root.querySelector('[data-fg-progress]');
    this.guidesWrap = root.querySelector('[data-fg-guides]');

    this.sortEntriesIntoPanes();
    this.hoistGuides();
    this.buildTwoColumnGuides();

    this.tiles = Array.prototype.slice.call(root.querySelectorAll('[data-fg-tile]'));
    this.guides = Array.prototype.slice.call(root.querySelectorAll('[data-fg-guide]'));

    /* The button renders with the empty-state label; the with-a-selection one only
       exists as a data attribute, since only JS ever knows the count. */
    this.ctaEmpty = this.ctaLabel ? this.ctaLabel.textContent.trim() : '';
    this.ctaFilled = root.getAttribute('data-cta-label-filled') || 'View your guides';
    this.statusLabel = root.getAttribute('data-fg-status-label') || '[count] guides selected';
    this.videoPauseLabel = root.getAttribute('data-fg-video-pause-label') || 'Pause videos';

    this.init();
  }

  /* Every block lands in the Product pane, since one content_for is all a section gets
     and it can't read its own theme blocks to sort them. Each tab has its own pane so
     its layout and padding are independent, so the entries are dealt out to their pane
     here, once, on load. Until then CSS keeps the Basics ones out of sight inside the
     Product pane, so no Basics tile ever flashes with the product grid's treatment.

     Every entry is re-appended, not just the ones changing pane, so that each pane ends
     up with the entries themselves as its direct children — the grid's dividers count
     cells with :nth-child, and a block the platform wrapped in a div of its own would
     otherwise both throw that count off and, once its entry moved out, sit there empty
     holding a cell open. The block's shopify_attributes are on the entry, so the theme
     editor still finds it. */
  FieldGuide.prototype.sortEntriesIntoPanes = function () {
    var self = this;
    if (!this.panes.length) return;

    this.panes.forEach(function (pane) {
      var entries = self.root.querySelectorAll(
        '[data-fg-entry][data-fg-tab="' + pane.dataset.fgPane + '"]'
      );
      var fragment = document.createDocumentFragment();

      Array.prototype.forEach.call(entries, function (entry) { fragment.appendChild(entry); });
      pane.appendChild(fragment);
    });

    this.panes.forEach(function (pane) {
      Array.prototype.slice.call(pane.children).forEach(function (child) {
        if (!child.hasAttribute('data-fg-entry')) pane.removeChild(child);
      });
    });
  };

  /* Each Guide block renders its tile and its guide body together, because a section
     can't read theme blocks and so can only receive them through one content_for. The
     tiles belong in the picker grid, so the bodies are lifted out of it here, once, into
     the reading view. They arrive `hidden`, so nothing flashes inside the grid first. */
  FieldGuide.prototype.hoistGuides = function () {
    var target = this.guidesWrap;
    if (!target) return;

    var nested = this.root.querySelectorAll('[data-fg-panes] [data-fg-guide]');
    if (!nested.length) return;

    var fragment = document.createDocumentFragment();
    Array.prototype.forEach.call(nested, function (guide) { fragment.appendChild(guide); });
    target.appendChild(fragment);
  };

  FieldGuide.prototype.buildTwoColumnGuides = function () {
    var self = this;
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-fg-guide]'), function (guide) {
      if (guide.classList.contains('fg-guide--split')) return;

      var header = guide.querySelector(':scope > .fg-guide__header');
      var body = guide.querySelector(':scope > .fg-guide__body');
      if (!header || !body) return;

      var columns = Array.prototype.slice.call(body.children);
      if (columns.length !== 2) return;

      var first = columns[0];
      var second = columns[1];
      var left = document.createElement('div');
      var right = document.createElement('div');

      left.className = 'fg-guide__left';
      right.className = 'fg-guide__right';

      guide.insertBefore(left, header);
      guide.insertBefore(right, body);
      left.appendChild(header);
      left.appendChild(first);
      right.appendChild(second);
      body.remove();

      guide.classList.add('fg-guide--split');
    });
  };

  /* The product grid's dashed dividers come from each cell's own border-top/-left (see
     skipper-field-guide.css), so a divider only exists where the neighbouring cell below
     or to the right is there to draw it. When the block count doesn't fill the last row,
     the cells that would have shared an edge with a missing cell need to draw that edge
     themselves instead, or the grid looks like it's missing a line rather than a product.
     Column count changes at the same 750px breakpoint the CSS uses, so this reruns on
     resize alongside the other breakpoint-driven syncs. */
  FieldGuide.prototype.syncGridEdgeBorders = function () {
    var pane = this.root.querySelector('.fg-pane--product');
    if (!pane) return;

    var entries = Array.prototype.slice.call(pane.children).filter(function (child) {
      return child.hasAttribute('data-fg-entry');
    });

    entries.forEach(function (entry) {
      entry.classList.remove('fg-entry--edge-bottom', 'fg-entry--edge-right');
    });

    var total = entries.length;
    if (!total) return;

    var columns = window.matchMedia('(min-width: 750px)').matches ? 5 : 3;
    var lastRowCount = total % columns;
    if (lastRowCount === 0) return;

    entries[total - 1].classList.add('fg-entry--edge-right');

    var bottomStart = Math.max(0, total - columns);
    var bottomEnd = total - lastRowCount;
    for (var i = bottomStart; i < bottomEnd; i += 1) {
      entries[i].classList.add('fg-entry--edge-bottom');
    }
  };

  /* A tab with no guides under it is not worth showing. */
  FieldGuide.prototype.pruneTabs = function () {
    var root = this.root;
    var entries = Array.prototype.slice.call(root.querySelectorAll('[data-fg-entry]'));
    var available = [];

    Array.prototype.forEach.call(root.querySelectorAll('[data-fg-tab-button]'), function (button) {
      var tab = button.dataset.fgTabButton;
      var has = entries.some(function (entry) { return entry.dataset.fgTab === tab; });
      button.hidden = !has;
      if (has) available.push(tab);
    });

    var tabs = root.querySelector('.fg-tabs');
    if (tabs) tabs.hidden = available.length < 2;

    return available;
  };

  FieldGuide.prototype.init = function () {
    var self = this;
    var fromUrl = readParam();
    var fromHash = this.isGuide(readHash()) ? readHash() : null;

    function keep(keys) {
      return keys.filter(function (key) { return self.isGuide(key); });
    }

    /* A deep link without a ?guides= set opens just its own guide, rather than joining
       whatever this visitor last picked. */
    if (fromUrl.length) {
      this.selected = keep(fromUrl);
    } else if (!fromHash) {
      this.selected = keep(readStored());
    }

    if (fromHash && this.selected.indexOf(fromHash) === -1) this.selected.push(fromHash);

    var available = this.pruneTabs();
    this.setTab(available.indexOf('product') !== -1 ? 'product' : available[0] || 'product');
    this.syncGridEdgeBorders();

    this.bind();
    this.initVideos();
    this.syncTiles();
    this.setView((fromUrl.length || fromHash) && this.selected.length ? 'results' : 'select', { replace: true });
    this.watchHeader();
    this.watchScrollReveals();

    if (fromHash) this.landOnGuide(fromHash);
  };

  FieldGuide.prototype.isGuide = function (key) {
    return !!key && this.guides.some(function (guide) { return guide.dataset.fgGuide === key; });
  };

  /* Bring a deep-linked guide into view below the sticky bars. The browser's own jump to
     the #howto- id can't do that: the guide was hidden when it tried, and it doesn't
     know about the sticky bars. It can also re-attempt that jump while the page is still
     loading, so the guide is re-aligned once more on load. */
  FieldGuide.prototype.landOnGuide = function (key) {
    var self = this;

    window.requestAnimationFrame(function () { self.scrollToGuide(key, true); });

    if (document.readyState !== 'complete') {
      window.addEventListener('load', function () { self.scrollToGuide(key, true); }, { once: true });
    }
  };

  FieldGuide.prototype.on = function (target, type, handler, options) {
    target.addEventListener(type, handler, options);
    this.listeners.push([target, type, handler, options]);
  };

  FieldGuide.prototype.bind = function () {
    var self = this;

    this.tiles.forEach(function (tile) {
      self.on(tile, 'click', function () {
        self.toggle(tile.dataset.fgTile, tile);
      });
    });

    Array.prototype.forEach.call(this.root.querySelectorAll('[data-fg-tab-button]'), function (button) {
      self.on(button, 'click', function () {
        self.setTab(button.dataset.fgTabButton);
      });
    });

    /* Only the active tab is in the Tab order (see setTab), so the arrow keys are how the
       others are reached — the standard tabs pattern, activating as focus moves. */
    var tablist = this.root.querySelector('.fg-tabs');
    if (tablist) {
      this.on(tablist, 'keydown', function (event) {
        var tabs = Array.prototype.slice.call(tablist.querySelectorAll('[data-fg-tab-button]'))
          .filter(function (tab) { return !tab.hidden; });
        var index = tabs.indexOf(document.activeElement);
        if (index === -1) return;

        var next;
        switch (event.key) {
          case 'ArrowRight': next = tabs[(index + 1) % tabs.length]; break;
          case 'ArrowLeft': next = tabs[(index - 1 + tabs.length) % tabs.length]; break;
          case 'Home': next = tabs[0]; break;
          case 'End': next = tabs[tabs.length - 1]; break;
          default: return;
        }

        event.preventDefault();
        self.setTab(next.dataset.fgTabButton);
        next.focus();
      });
    }

    Array.prototype.forEach.call(this.root.querySelectorAll('[data-fg-panel-toggle]'), function (button) {
      self.on(button, 'click', function () {
        var panel = button.closest('[data-fg-panel]');
        if (!panel) return;
        var open = panel.classList.toggle('is-open');
        button.setAttribute('aria-expanded', open ? 'true' : 'false');

        /* A collapsed panel is only squashed to zero height, so it is made inert too —
           otherwise its links and videos stay tabbable and read out while invisible. */
        var content = panel.querySelector('[data-fg-panel-content]');
        if (content) content.inert = !open;
        window.requestAnimationFrame(function () { self.syncGuideScrollFades(); });
        window.setTimeout(function () { self.syncGuideScrollFades(); }, 420);
      });
    });

    Array.prototype.forEach.call(this.root.querySelectorAll('.fg-guide__right'), function (column) {
      self.on(column, 'scroll', function () { self.syncGuideScrollFades(); }, { passive: true });
    });

    this.on(window, 'resize', function () {
      self.syncProductGuideHeights();
      self.syncGuideScrollFades();
      self.syncGridEdgeBorders();
    });

    Array.prototype.forEach.call(this.root.querySelectorAll('[data-fg-chat]'), function (button) {
      self.on(button, 'click', function () {
        /* The chat integration is not built yet. Fire an event a future widget can hook,
           and fall back to the configured link when nothing cancels it. */
        var event = new CustomEvent('skipper:field-guide:chat', {
          bubbles: true,
          cancelable: true,
          detail: { selected: self.selected.slice() }
        });
        var proceed = button.dispatchEvent(event);
        var link = button.getAttribute('data-fg-chat-link');
        if (proceed && link) window.location.href = link;
      });
    });

    if (this.actions) {
      var requestActionsContrast = this.requestActionsContrast.bind(this);

      this.on(window, 'scroll', requestActionsContrast, { passive: true });
      this.on(window, 'resize', requestActionsContrast);
    }

    if (this.ctaButton) {
      this.on(this.ctaButton, 'click', function () {
        if (!self.selected.length) return;
        self.setView('results');
        self.scrollToTop();
        self.focusView();
      });
    }

    if (this.pills) {
      var requestJumpSync = this.requestJumpSync.bind(this);

      this.on(this.pills, 'scroll', requestJumpSync, { passive: true });
      this.on(window, 'resize', requestJumpSync);
      this.bindPillDrag();
    }

    var back = this.root.querySelector('[data-fg-back]');
    if (back) {
      this.on(back, 'click', function () {
        self.setView('select');
        self.scrollToTop();
        self.focusView();
      });
    }

    /* Reselecting takes you back to the same picker page you started from, rather than
       opening it as a popup over the guides. */
    var reselect = this.root.querySelector('[data-fg-reselect]');
    if (reselect) {
      this.on(reselect, 'click', function () {
        self.setView('select');
        self.scrollToTop();
        self.focusView();
      });
    }

    /* Back/forward between a guide set and the picker. */
    this.on(window, 'popstate', function () {
      var keys = readParam();
      if (!keys.length && readHash()) keys = [readHash()];

      self.selected = keys.filter(function (key) { return self.isGuide(key); });
      self.syncTiles();
      self.setView(self.selected.length ? 'results' : 'select', { silent: true });
    });

    /* A #howto- link followed while already on the page (or typed into the address
       bar) adds its guide to the current set and goes to it. The browser has already
       made the history entry, so the ?guides= sync replaces it rather than adding one. */
    this.on(window, 'hashchange', function () {
      var key = readHash();
      if (!self.isGuide(key)) return;

      if (self.selected.indexOf(key) === -1) self.selected.push(key);
      self.syncTiles();
      self.setView('results', { replace: true });

      window.requestAnimationFrame(function () { self.scrollToGuide(key); });
    });
  };

  /* Touch and trackpads already scroll the jump bar, but a mouse wheel only scrolls it
     sideways with Shift, so the pills can also be dragged. Mouse only — touch keeps its
     native scroll. A drag past DRAG_THRESHOLD swallows the click that ends it, so letting
     go over a pill doesn't also jump to that guide. */
  var DRAG_THRESHOLD = 5;

  FieldGuide.prototype.bindPillDrag = function () {
    var self = this;
    var pills = this.pills;
    var drag = null;

    this.on(pills, 'pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (pills.scrollWidth - pills.clientWidth <= 1) return;
      drag = { x: event.clientX, left: pills.scrollLeft, moved: false };
    });

    this.on(window, 'pointermove', function (event) {
      if (!drag) return;
      var dx = event.clientX - drag.x;

      if (!drag.moved) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        drag.moved = true;
        pills.classList.add('is-dragging');
      }

      pills.scrollLeft = drag.left - dx;
    });

    function end() {
      if (!drag) return;
      var moved = drag.moved;
      drag = null;
      pills.classList.remove('is-dragging');

      /* The click (if any) fires straight after pointerup, before this timeout, so a
         drag released off the bar can't go on to swallow some later, real click. */
      self.pillDragged = moved;
      if (moved) window.setTimeout(function () { self.pillDragged = false; }, 0);
    }

    this.on(window, 'pointerup', end);
    this.on(window, 'pointercancel', end);

    /* Capture phase, so it lands before the pill's own click handler. */
    this.on(pills, 'click', function (event) {
      if (!self.pillDragged) return;
      self.pillDragged = false;
      event.preventDefault();
      event.stopPropagation();
    }, true);
  };

  FieldGuide.prototype.toggle = function (key, tile) {
    var index = this.selected.indexOf(key);

    if (index === -1) {
      this.selected.push(key);
      if (tile && !prefersReducedMotion()) {
        tile.classList.remove('is-just-checked');
        /* Restart the pop even when the same tile is re-ticked. */
        void tile.offsetWidth;
        tile.classList.add('is-just-checked');
      }
    } else {
      this.selected.splice(index, 1);
    }

    this.syncTiles();
    this.announceSelection();
    writeStored(this.selected);
  };

  FieldGuide.prototype.syncTiles = function () {
    var self = this;

    this.tiles.forEach(function (tile) {
      var on = self.selected.indexOf(tile.dataset.fgTile) !== -1;
      tile.setAttribute('aria-checked', on ? 'true' : 'false');
    });

    if (this.ctaButton && this.ctaLabel) {
      var count = this.selected.length;
      this.ctaButton.disabled = count === 0;
      this.ctaLabel.textContent = count === 0 ? this.ctaEmpty : this.ctaFilled + ' (' + count + ')';
    }
  };

  /* Tells screen readers the tick registered, since the count otherwise only shows on
     the CTA. Kept out of syncTiles so page load and back/forward don't announce. */
  FieldGuide.prototype.announceSelection = function () {
    if (!this.status) return;
    this.status.textContent = this.statusLabel.replace('[count]', this.selected.length);
  };

  FieldGuide.prototype.setTab = function (tab) {
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-fg-tab-button]'), function (button) {
      var on = button.dataset.fgTabButton === tab;
      button.setAttribute('aria-selected', on ? 'true' : 'false');
      button.tabIndex = on ? 0 : -1;
    });

    this.panes.forEach(function (pane) {
      pane.classList.toggle('is-active', pane.dataset.fgPane === tab);
    });

    this.requestActionsContrast();
  };

  /* The sticky CTA and chat button are accent yellow, so riding over a yellow Basics card
     they all but disappear into it. While either overlaps one, the row is flagged and
     the CSS turns them white — see .fg-actions.is-on-accent. */
  FieldGuide.prototype.requestActionsContrast = function () {
    if (this.contrastSyncing || !this.actions) return;
    this.contrastSyncing = true;

    var self = this;
    window.requestAnimationFrame(function () {
      self.contrastSyncing = false;
      self.syncActionsContrast();
    });
  };

  FieldGuide.prototype.syncActionsContrast = function () {
    var actions = this.actions;
    var on = false;

    if (this.root.dataset.view === 'select') {
      var buttons = Array.prototype.slice.call(actions.querySelectorAll('.fg-cta, .fg-chat'));
      var cards = this.root.querySelectorAll('.fg-pane.is-active .fg-tile--basics');

      on = Array.prototype.some.call(cards, function (card) {
        var box = card.getBoundingClientRect();
        return buttons.some(function (button) {
          var rect = button.getBoundingClientRect();
          return rect.bottom > box.top && rect.top < box.bottom &&
            rect.right > box.left && rect.left < box.right;
        });
      });
    }

    actions.classList.toggle('is-on-accent', on);
  };

  FieldGuide.prototype.setView = function (view, options) {
    var self = this;
    options = options || {};
    this.root.dataset.view = view;
    document.documentElement.setAttribute('data-field-guide-view', view);

    /* Visibility itself is CSS's job, off the attribute above — see the
       [data-field-guide-view] rules in skipper-field-guide.css. */
    if (view === 'results') {
      this.renderGuides();
      this.renderPills();
      this.observeGuides();
      this.watchProgress();
      if (this.measureScrollReveals) this.measureScrollReveals();
      window.requestAnimationFrame(function () {
        self.syncProductGuideHeights();
        self.syncGuideScrollFades();
      });
    }

    this.requestActionsContrast();
    if (!options.silent) this.pushUrl(view, options.replace);
  };

  /* The button that switched views has just been hidden, which would drop keyboard and
     screen reader focus back to the top of the document. Land it on the new view's
     heading instead — the results heading, else the first guide shown; the intro
     heading, else the picker. preventScroll, since scrollToTop is already moving there. */
  FieldGuide.prototype.focusView = function () {
    var target;

    if (this.root.dataset.view === 'results') {
      target = this.root.querySelector('[data-fg-results] [data-fg-view-focus]');
      if (!target) {
        var first = this.guides.filter(function (guide) { return !guide.hidden; })[0];
        target = first && first.querySelector('.fg-guide__title');
      }
    } else {
      target = this.root.querySelector('[data-fg-select-only] [data-fg-view-focus]') ||
        this.root.querySelector('.fg-picker__sheet');
    }

    if (target) target.focus({ preventScroll: true });
  };

  FieldGuide.prototype.pushUrl = function (view, replace) {
    var url = new URL(window.location.href);

    if (view === 'results' && this.selected.length) {
      url.searchParams.set(PARAM, this.selected.join(','));
    } else {
      url.searchParams.delete(PARAM);
    }

    /* Drop a #howto- link once it no longer points at a guide on screen, or reloading
       would reopen it. */
    var hashKey = readHash();
    if (hashKey && (view !== 'results' || this.selected.indexOf(hashKey) === -1)) url.hash = '';

    if (url.href === window.location.href) return;
    window.history[replace ? 'replaceState' : 'pushState']({}, '', url.href);
  };

  FieldGuide.prototype.renderGuides = function () {
    var selected = this.selected;

    /* Guides stay in the static order hoistGuides put them in — the order the Guide
       blocks are arranged in the theme editor — regardless of the order they were
       ticked in. Only visibility changes here; the leading rule is a class rather than
       :first-of-type, since that would land on a hidden guide. */
    this.guides.forEach(function (guide) {
      guide.hidden = selected.indexOf(guide.dataset.fgGuide) === -1;
    });

    this.guides.forEach(function (guide) { guide.classList.remove('is-first'); });
    var first = this.guides.filter(function (guide) { return !guide.hidden; })[0];
    if (first) first.classList.add('is-first');
  };

  /* Product guides pin their height to the header + desktop video (the left column),
     so the instructions column (right) scrolls internally rather than growing the
     guide taller. That height varies per guide (title length, viewport width via the
     video's clamp()), so it's measured here rather than hardcoded — see
     --fg-guide-height on .fg-guide--split in the CSS. */
  FieldGuide.prototype.syncProductGuideHeights = function () {
    Array.prototype.forEach.call(
      this.root.querySelectorAll('.fg-guide--split'),
      function (guide) {
        if (guide.hidden) return;
        var left = guide.querySelector(':scope > .fg-guide__left');
        if (!left) return;
        guide.style.setProperty('--fg-guide-height', left.offsetHeight + 'px');
      }
    );
  };

  /* Fade only the content that has actually scrolled beyond an instruction column's
     visible edge. This avoids a permanent fade when the column has no overflow. How
     deep the fade runs is the stylesheet's call (--fg-scroll-fade-size); each edge is
     only switched on and off here. */
  FieldGuide.prototype.syncGuideScrollFades = function () {
    var fade = 'var(--fg-scroll-fade-size, 4rem)';

    Array.prototype.forEach.call(this.root.querySelectorAll('.fg-guide__right'), function (column) {
      var maxScroll = column.scrollHeight - column.clientHeight;
      var hasOverflow = maxScroll > 1;
      var topFade = hasOverflow && column.scrollTop > 1 ? fade : '0px';
      var bottomFade = hasOverflow && column.scrollTop < maxScroll - 1 ? fade : '0px';

      column.style.setProperty('--fg-scroll-fade-top', topFade);
      column.style.setProperty('--fg-scroll-fade-bottom', bottomFade);
    });
  };

  FieldGuide.prototype.renderPills = function () {
    if (!this.pills) return;

    var self = this;
    this.pills.innerHTML = '';

    /* Same static, block-order pass as renderGuides, so the jump bar matches the order
       the guides actually read in rather than the order they were ticked. */
    this.guides.forEach(function (guide) {
      var key = guide.dataset.fgGuide;
      if (self.selected.indexOf(key) === -1) return;

      var title = guide.querySelector('.fg-guide__title');
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'fg-jump__pill';
      button.dataset.fgJumpTo = key;
      button.textContent = title ? title.textContent.trim() : key;

      button.addEventListener('click', function () {
        self.scrollToGuide(key);
        self.focusGuide(key);
      });

      self.pills.appendChild(button);
    });

    this.requestJumpSync();
  };

  /* What a scrolled-to guide has to clear: the jump bar, plus whatever of the site
     header is on screen underneath it. */
  FieldGuide.prototype.stickyOffset = function () {
    var header = parseFloat(
      getComputedStyle(this.root).getPropertyValue('--fg-header-offset')
    ) || 0;
    var bar = this.jump ? this.jump.offsetHeight : 0;
    return header + bar;
  };

  FieldGuide.prototype.scrollToGuide = function (key, instant) {
    var guide = this.guides.filter(function (item) { return item.dataset.fgGuide === key; })[0];
    if (!guide) return;

    var top = guide.getBoundingClientRect().top + window.pageYOffset - this.stickyOffset() - 16;
    window.scrollTo({
      top: top,
      behavior: instant || prefersReducedMotion() ? 'auto' : 'smooth'
    });
  };

  /* Move focus along with the scroll, so the next Tab (or a screen reader's reading
     position) carries on from the guide rather than the jump bar. */
  FieldGuide.prototype.focusGuide = function (key) {
    var guide = this.guides.filter(function (item) { return item.dataset.fgGuide === key; })[0];
    var title = guide && guide.querySelector('.fg-guide__title');
    if (title) title.focus({ preventScroll: true });
  };

  FieldGuide.prototype.requestJumpSync = function () {
    if (this.jumpSyncing) return;
    this.jumpSyncing = true;

    var self = this;
    window.requestAnimationFrame(function () {
      self.jumpSyncing = false;
      self.syncJumpScroll();
    });
  };

  FieldGuide.prototype.syncJumpScroll = function () {
    if (!this.jump || !this.pills) return;

    var max = this.pills.scrollWidth - this.pills.clientWidth;
    var canScroll = max > 1;
    var left = this.pills.scrollLeft;
    var atStart = left <= 1;
    var atEnd = left >= max - 1;

    this.jump.classList.toggle('has-jump-scroll', canScroll);
    this.jump.classList.toggle('has-jump-scroll-left', canScroll && !atStart);
    this.jump.classList.toggle('has-jump-scroll-right', canScroll && !atEnd);

    this.pills.style.setProperty('--fg-jump-fade-left', canScroll && !atStart ? '3.6rem' : '0px');
    this.pills.style.setProperty('--fg-jump-fade-right', canScroll && !atEnd ? '3.6rem' : '0px');
  };

  FieldGuide.prototype.scrollToTop = function () {
    var top = this.root.getBoundingClientRect().top + window.pageYOffset - 16;
    if (window.pageYOffset <= top) return;
    window.scrollTo({ top: top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  /* ---------------------------------------------------------------- observers */

  FieldGuide.prototype.disconnect = function () {
    this.observers.forEach(function (observer) { observer.disconnect(); });
    this.observers = [];
  };

  FieldGuide.prototype.track = function (observer) {
    this.observers.push(observer);
    return observer;
  };

  /* The outro photos drift up into place once they are well up the screen: a photo is
     revealed when its top edge passes 75% down the viewport, then the movement itself
     plays on a timed CSS transition rather than tracking the scroll, so it stays smooth
     however fast you are scrolling. It resets once the photo drops back below
     the fold, so it replays on the next pass. JS only toggles classes; the animation is
     CSS's — see [data-fg-scroll-reveal].

     The outro is a separate section, so outside this.root. It is display:none while the
     picker is up, which measures 0×0 and is skipped — never armed, so left showing — and
     setView re-measures on the switch to results. */
  FieldGuide.prototype.watchScrollReveals = function () {
    if (prefersReducedMotion()) return;

    var outro = document.querySelector('[data-field-guide-results-only]');
    var frames = outro ? Array.prototype.slice.call(outro.querySelectorAll('[data-fg-scroll-reveal]')) : [];
    if (!frames.length) return;

    var ticking = false;

    function measure() {
      ticking = false;
      var viewport = window.innerHeight;
      frames.forEach(function (frame) {
        var box = frame.getBoundingClientRect();
        if (!box.width && !box.height) return;
        frame.classList.add('is-armed');
        if (box.top < viewport * 0.75) {
          frame.classList.add('is-revealed');
        } else if (box.top > viewport) {
          frame.classList.remove('is-revealed');
        }
      });
    }

    function request() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(measure);
    }

    this.measureScrollReveals = request;
    this.on(window, 'scroll', request, { passive: true });
    this.on(window, 'resize', request);
    measure();
  };

  /* Marks the jump pill for whichever guide is under the sticky bar. */
  FieldGuide.prototype.observeGuides = function () {
    if (!('IntersectionObserver' in window) || !this.pills) return;

    var self = this;
    var visible = this.guides.filter(function (guide) { return !guide.hidden; });
    if (!visible.length) return;

    /* Reselecting re-runs this, so the previous observer is dropped first. */
    if (this.guideObserver) this.guideObserver.disconnect();

    this.guideObserver = this.track(
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var key = entry.target.dataset.fgGuide;
            Array.prototype.forEach.call(self.pills.children, function (pill) {
              var active = pill.dataset.fgJumpTo === key;
              pill.classList.toggle('is-active', active);
              if (active) {
                pill.setAttribute('aria-current', 'true');
                self.revealPill(pill);
              } else {
                pill.removeAttribute('aria-current');
              }
            });
          });
        },
        { rootMargin: '-25% 0px -60% 0px', threshold: 0 }
      )
    );

    visible.forEach(function (guide) { self.guideObserver.observe(guide); });

  };

  /* On phones the jump bar only fits a few pills, so as the reading position moves on
     the active one is slid into the middle of the row rather than left off-screen.
     Scrolls the row itself, never the page (scrollIntoView would do both). Desktop is
     left alone, where the row is usually wide enough and can be dragged by hand. */
  FieldGuide.prototype.revealPill = function (pill) {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    var row = this.pills;
    var max = row.scrollWidth - row.clientWidth;
    if (max <= 1) return;

    var left = pill.offsetLeft - (row.clientWidth - pill.offsetWidth) / 2;
    row.scrollTo({
      left: Math.max(0, Math.min(max, left)),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth'
    });
  };

  /* The theme's header is sticky-on-scroll-up, so the jump bar tracks whatever part of
     it is actually on screen rather than a fixed height — see --fg-header-offset in the
     stylesheet.

     Scroll and resize are not enough on their own: the header slides on a CSS
     transition (`.section-header.animate { transition: top 0.15s ease-out }`), which
     carries on after the scroll that triggered it has stopped. Measuring only on scroll
     left the bar parked at a stale offset for the rest of the slide — overlapping the
     header on the way down, or floating below where it belongs on the way up. So a
     transition on the header drives the same measurement each frame until it ends. */
  FieldGuide.prototype.watchHeader = function () {
    var self = this;
    var header = document.querySelector('.section-header, sticky-header');
    if (!header) return;

    var ticking = false;
    var sliding = false;

    function measure() {
      ticking = false;
      var bottom = Math.max(0, header.getBoundingClientRect().bottom);
      self.root.style.setProperty('--fg-header-offset', bottom + 'px');
      if (sliding) request();
    }

    function request() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(measure);
    }

    /* `top` is the only property the header transitions, but guard anyway so an
       unrelated transition on a descendant can't leave the loop running. */
    function onSlideStart(event) {
      if (event.target !== header || event.propertyName !== 'top') return;
      sliding = true;
      request();
    }

    function onSlideEnd(event) {
      if (event.target !== header || event.propertyName !== 'top') return;
      sliding = false;
      request();
    }

    this.on(window, 'scroll', request, { passive: true });
    this.on(window, 'resize', request);
    this.on(header, 'transitionrun', onSlideStart);
    this.on(header, 'transitionend', onSlideEnd);
    this.on(header, 'transitioncancel', onSlideEnd);
    measure();
  };

  FieldGuide.prototype.watchProgress = function () {
    if (!this.progress || !this.guidesWrap || this.progressBound) return;

    var self = this;
    var ticking = false;

    function measure() {
      ticking = false;
      var box = self.guidesWrap.getBoundingClientRect();
      var travelled = -box.top + window.innerHeight * 0.5;
      var total = box.height;
      var ratio = total > 0 ? Math.min(1, Math.max(0, travelled / total)) : 0;
      self.progress.style.width = (ratio * 100).toFixed(2) + '%';
    }

    function request() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(measure);
    }

    this.on(window, 'scroll', request, { passive: true });
    this.on(window, 'resize', request);
    this.progressBound = true;
    measure();
  };

  FieldGuide.prototype.destroy = function () {
    this.videoSequences.forEach(function (sequence) { sequence.stop(); });
    this.videoSequences = [];
    this.disconnect();
    this.measureScrollReveals = null;
    this.guideObserver = null;
    this.listeners.forEach(function (entry) {
      entry[0].removeEventListener(entry[1], entry[2], entry[3]);
    });
    this.listeners = [];
  };

  /* ---------------------------------------------------------------- videos */

  /* No video on the page autoplays from its markup (blocks/fg-image.liquid,
     blocks/fg-media.liquid, snippets/field-guide-tile.liquid). Every guide body is in the
     DOM at once, so autoplay had the browser downloading and decoding every video on the
     page together, even the ones display:none at the current breakpoint. Instead each
     video sits at preload="none", is fetched only as it nears the viewport, and plays
     only while it is on screen.

     The videos in an Image strip are its numbered step clips, often cut from one source
     video with start/end timestamps. They play in order, then loop back to the first.
     Each clip starts VIDEO_OVERLAP before the previous one ends, so for that moment two
     are decoding; otherwise only one is, and the others rest paused on their own clip's
     first frame, which is their thumbnail. With the section's "Play step videos in
     sequence" setting off (data-fg-videos-together), every clip in a strip instead plays
     at once, each looping its own range. Any other video (the desktop guide
     video, the Basics tile videos) is a sequence of one, so it just loops itself. */

  /* How far ahead of the viewport a video starts fetching its first frame, and how much
     of it has to be on screen before it plays. */
  var VIDEO_PRIME_MARGIN = '300px 0px';
  var VIDEO_PLAY_THRESHOLD = 0.25;

  /* A clip hands over this far before its end time. timeupdate only fires every
     ~250ms, so the end is checked every frame instead (see VideoSequence#watch). */
  var VIDEO_END_TOLERANCE = 0.05;

  /* How long before a step clip ends the next one starts, so they briefly play together.
     Capped at half the clip, so a very short clip still plays before handing over. */
  var VIDEO_OVERLAP = 0.5;

  function VideoSequence(owner, container, frames, together) {
    var self = this;

    this.container = container;
    this.together = together;
    this.index = 0;
    this.inView = false;
    this.primed = false;
    this.userPaused = false;
    this.userPlayed = false;
    this.frame = null;
    this.outgoing = null;

    this.clips = frames.map(function (frame) {
      return {
        video: frame.querySelector('video'),
        start: parseTimestamp(frame.getAttribute('data-fg-video-start')),
        end: parseTimestamp(frame.getAttribute('data-fg-video-end'))
      };
    }).filter(function (clip) { return clip.video; });

    this.clips.forEach(function (clip) {
      var video = clip.video;
      video.muted = true;
      video.playsInline = true;
      video.loop = false;
      self.watchReady(owner, clip);

      /* No native controls: clicking a video (or Enter/Space once focused) pauses and
         resumes its whole sequence. A Basics tile's video is left alone, since a click
         there selects the tile. */
      if (video.closest('[data-fg-tile]')) return;

      /* Announced as a "Pause videos" toggle button, pressed while paused. */
      video.tabIndex = 0;
      video.setAttribute('role', 'button');
      video.setAttribute('aria-label', owner.videoPauseLabel);
      video.classList.add('fg-video--toggle');
      owner.on(video, 'click', function () { self.toggle(); });
      owner.on(video, 'keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        self.toggle();
      });
    });

    this.syncPressed();
  }

  /* Hold a video hidden over its blurred preview (see [data-fg-video]::before in
     skipper-field-guide.css) until it has a frame worth showing, then fade it in. A
     clip trimmed to a later start waits for its seek, since until then it shows the
     source video's opening frame (or poster), not its own. */
  VideoSequence.prototype.watchReady = function (owner, clip) {
    var frame = clip.video.closest('[data-fg-video]');
    if (!frame) return;

    clip.trimmed = clip.start != null && clip.start > 0;
    clip.reveal = function () { frame.classList.remove('is-video-pending'); };

    if (!clip.trimmed && clip.video.readyState >= 2) return;
    frame.classList.add('is-video-pending');

    (clip.trimmed ? ['seeked', 'playing'] : ['loadeddata', 'seeked', 'playing']).forEach(function (type) {
      owner.on(clip.video, type, clip.reveal);
    });
  };

  /* Pressed = held paused: by the visitor, or by reduced motion until they press play. */
  VideoSequence.prototype.syncPressed = function () {
    var pressed = this.userPaused || (prefersReducedMotion() && !this.userPlayed);

    this.clips.forEach(function (clip) {
      if (clip.video.getAttribute('role') !== 'button') return;
      clip.video.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    });
  };

  /* A visitor's pause holds, even through scrolling away and back, until they click
     again. Their play goes ahead regardless of prefers-reduced-motion. */
  VideoSequence.prototype.toggle = function () {
    var current = this.clips[this.index];

    /* Go by the video itself, not userPaused: under reduced motion (or refused
       autoplay) it was never playing, so the first click should start it. */
    if (!current || current.video.paused) {
      this.userPaused = false;
      this.userPlayed = true;
      this.syncPressed();
      this.play();
      return;
    }

    this.userPaused = true;
    this.syncPressed();
    if (this.frame) window.cancelAnimationFrame(this.frame);
    this.frame = null;
    this.finishOutgoing();
    this.clips.forEach(function (clip) { clip.video.pause(); });
  };

  VideoSequence.prototype.range = function (clip) {
    var duration = clip.video.duration;
    var start = clip.start != null ? Math.max(0, clip.start) : 0;
    var end = clip.end != null ? clip.end : duration;

    if (isFinite(duration)) end = Math.min(end, duration);
    if (!isFinite(end) || end <= start) return null;

    return { start: start, end: end };
  };

  /* Park a clip on its first frame. Seeking also clears the poster, so a paused clip
     shows its own start frame rather than the source video's opening one. */
  VideoSequence.prototype.rewind = function (clip) {
    var range = this.range(clip);
    if (!range || clip.video.readyState < 1) return;
    if (Math.abs(clip.video.currentTime - range.start) < 0.01) return;

    try {
      clip.video.currentTime = range.start;
    } catch (error) {
      /* Not seekable yet; it is rewound again before it next plays. */
    }
  };

  /* Fetch just enough of every clip to show its first frame. */
  VideoSequence.prototype.prime = function () {
    if (this.primed) return;
    this.primed = true;

    var self = this;
    this.clips.forEach(function (clip) {
      var video = clip.video;

      /* Some browsers don't decode a frame on preload="metadata" (so no loadeddata),
         but an untrimmed clip's poster is its own first frame, so once that has loaded
         it's fine to show. */
      if (clip.reveal && !clip.trimmed && video.poster) {
        var poster = new Image();
        poster.onload = clip.reveal;
        poster.src = video.poster;
      }

      if (video.readyState >= 1) {
        self.rewind(clip);
        return;
      }

      video.addEventListener('loadedmetadata', function () {
        if (video.paused) self.rewind(clip);
      }, { once: true });
      video.preload = 'metadata';
      video.load();
    });
  };

  VideoSequence.prototype.play = function () {
    if (!this.inView || document.hidden || this.userPaused) return;
    if (prefersReducedMotion() && !this.userPlayed) return;
    this.prime();

    if (this.together) {
      this.playTogether();
      return;
    }

    var self = this;
    var clip = this.clips[this.index];
    var video = clip.video;

    if (video.readyState < 1) {
      if (!clip.waiting) {
        clip.waiting = true;
        video.addEventListener('loadedmetadata', function () {
          clip.waiting = false;
          if (self.clips[self.index] === clip) self.play();
        }, { once: true });
      }
      return;
    }

    var range = this.range(clip);
    if (range && (video.currentTime < range.start || video.currentTime >= range.end - VIDEO_END_TOLERANCE)) {
      this.rewind(clip);
    }

    /* Let the next clip buffer while this one plays, so the handover doesn't stall. */
    var next = this.clips[(this.index + 1) % this.clips.length];
    if (next !== clip) next.video.preload = 'auto';

    video.play().catch(function () {
      /* Autoplay refused (e.g. Low Power Mode), so it stays on its first frame. */
    });
    this.watch();
  };

  /* Every clip plays at once, each looping its own range (see watchTogether). */
  VideoSequence.prototype.playTogether = function () {
    var self = this;

    this.clips.forEach(function (clip) {
      var video = clip.video;

      if (video.readyState < 1) {
        if (!clip.waiting) {
          clip.waiting = true;
          video.addEventListener('loadedmetadata', function () {
            clip.waiting = false;
            self.play();
          }, { once: true });
        }
        return;
      }

      var range = self.range(clip);
      if (range && (video.currentTime < range.start || video.currentTime >= range.end - VIDEO_END_TOLERANCE)) {
        self.rewind(clip);
      }

      video.preload = 'auto';
      if (video.paused) {
        video.play().catch(function () {
          /* Autoplay refused (e.g. Low Power Mode), so it stays on its first frame. */
        });
      }
    });

    this.watchTogether();
  };

  VideoSequence.prototype.watchTogether = function () {
    var self = this;
    if (this.frame) window.cancelAnimationFrame(this.frame);

    function tick() {
      self.frame = null;
      var playing = false;

      self.clips.forEach(function (clip) {
        if (clip.video.paused) return;
        playing = true;

        var range = self.range(clip);
        if (range && !clip.video.seeking && clip.video.currentTime >= range.end - VIDEO_END_TOLERANCE) {
          self.rewind(clip);
        }
      });

      if (playing) self.frame = window.requestAnimationFrame(tick);
    }

    this.frame = window.requestAnimationFrame(tick);
  };

  /* How early a clip hands over to the next. A sequence of one just loops itself. */
  VideoSequence.prototype.overlap = function (range) {
    if (this.clips.length < 2) return VIDEO_END_TOLERANCE;
    return Math.max(VIDEO_END_TOLERANCE, Math.min(VIDEO_OVERLAP, (range.end - range.start) / 2));
  };

  VideoSequence.prototype.watch = function () {
    var self = this;
    if (this.frame) window.cancelAnimationFrame(this.frame);

    function tick() {
      self.frame = null;

      /* The previous clip plays out its last moment alongside the current one. */
      var outgoing = self.outgoing;
      if (outgoing) {
        var outgoingRange = self.range(outgoing);
        if (outgoing.video.paused || outgoing.video.ended || !outgoingRange ||
            outgoing.video.currentTime >= outgoingRange.end - VIDEO_END_TOLERANCE) {
          self.finishOutgoing();
        }
      }

      var clip = self.clips[self.index];
      var range = self.range(clip);

      if (range && !clip.video.seeking && !clip.video.paused &&
          clip.video.currentTime >= range.end - self.overlap(range)) {
        self.advance();
        return;
      }

      if (!clip.video.paused || self.outgoing) self.frame = window.requestAnimationFrame(tick);
    }

    this.frame = window.requestAnimationFrame(tick);
  };

  /* Start the next clip while the current one plays out its overlap. */
  VideoSequence.prototype.advance = function () {
    var current = this.clips[this.index];
    this.finishOutgoing();

    if (this.clips.length > 1) {
      this.outgoing = current;
    } else {
      current.video.pause();
      this.rewind(current);
    }

    this.index = (this.index + 1) % this.clips.length;
    this.play();
  };

  /* Park the clip that was playing out its overlap back on its first frame. */
  VideoSequence.prototype.finishOutgoing = function () {
    var outgoing = this.outgoing;
    if (!outgoing) return;
    this.outgoing = null;

    if (!outgoing.video.paused) outgoing.video.pause();
    this.rewind(outgoing);
  };

  /* Off screen (or tab hidden): pause, park on the first frame, and start again from
     the first clip next time. */
  VideoSequence.prototype.stop = function () {
    if (this.frame) window.cancelAnimationFrame(this.frame);
    this.frame = null;
    this.finishOutgoing();

    /* Rewound even if already paused (e.g. by the visitor), since the sequence restarts
       from its first clip and this one would otherwise stay stuck mid-clip. */
    var self = this;
    var clips = this.together ? this.clips : [this.clips[this.index]];
    clips.forEach(function (clip) {
      if (!clip) return;
      if (!clip.video.paused) clip.video.pause();
      self.rewind(clip);
    });

    this.index = 0;
  };

  FieldGuide.prototype.initVideos = function () {
    var self = this;
    var claimed = [];
    var together = this.root.hasAttribute('data-fg-videos-together');

    Array.prototype.forEach.call(this.root.querySelectorAll('[data-fg-video-sequence]'), function (container) {
      var frames = Array.prototype.slice.call(container.querySelectorAll('[data-fg-video]'));
      if (!frames.length) return;
      claimed = claimed.concat(frames);
      self.videoSequences.push(new VideoSequence(self, container, frames, together));
    });

    Array.prototype.forEach.call(this.root.querySelectorAll('[data-fg-video]'), function (frame) {
      if (claimed.indexOf(frame) !== -1) return;
      self.videoSequences.push(new VideoSequence(self, frame, [frame], false));
    });

    this.videoSequences = this.videoSequences.filter(function (sequence) { return sequence.clips.length; });
    if (!this.videoSequences.length) return;

    function sequenceFor(element) {
      return self.videoSequences.filter(function (sequence) { return sequence.container === element; })[0];
    }

    if (!('IntersectionObserver' in window)) {
      this.videoSequences.forEach(function (sequence) {
        sequence.inView = true;
        sequence.play();
      });
      return;
    }

    /* Anything hidden at this breakpoint, or in a guide that isn't picked, never
       intersects, so neither observer touches it and it is never fetched. */
    var primer = this.track(new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var sequence = sequenceFor(entry.target);
        if (sequence) sequence.prime();
        primer.unobserve(entry.target);
      });
    }, { rootMargin: VIDEO_PRIME_MARGIN }));

    var player = this.track(new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var sequence = sequenceFor(entry.target);
        if (!sequence) return;

        var inView = entry.isIntersecting && entry.intersectionRatio >= VIDEO_PLAY_THRESHOLD;
        if (inView === sequence.inView) return;

        sequence.inView = inView;
        if (inView) {
          sequence.play();
        } else {
          sequence.stop();
        }
      });
    }, { threshold: [0, VIDEO_PLAY_THRESHOLD] }));

    this.videoSequences.forEach(function (sequence) {
      primer.observe(sequence.container);
      player.observe(sequence.container);
    });

    this.on(document, 'visibilitychange', function () {
      self.videoSequences.forEach(function (sequence) {
        if (document.hidden) {
          sequence.stop();
        } else {
          sequence.play();
        }
      });
    });
  };

  /* ---------------------------------------------------------------- theme editor */

  /* Guides are only on screen once someone has picked them, so selecting a Guide block —
     or anything nested in one — would otherwise scroll the editor to something invisible.
     Reveal that guide instead, adding it to the selection if it isn't already in it. */
  FieldGuide.prototype.revealBlock = function (element) {
    var guide = element.closest('[data-fg-guide]');
    var key = guide ? guide.dataset.fgGuide : null;

    if (!key) {
      var entry = element.closest('[data-fg-entry]');
      key = entry ? entry.dataset.fgEntry : null;
      /* A tile is only visible in the picker, so stay there. */
      if (key) {
        this.setView('select');
        return;
      }
    }

    if (!key) return;

    if (this.selected.indexOf(key) === -1) {
      this.selected.push(key);
      this.syncTiles();
    }

    this.setView('results');
    this.scrollToGuide(key);
  };

  /* ---------------------------------------------------------------- boot */

  /* The sign-off is its own section and so sits outside every controller root, which is
     why this is delegated from the document once rather than bound per instance. */
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest || !target.closest('[data-fg-scroll-top]')) return;

    event.preventDefault();
    window.scrollTo({
      top: 0,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  });

  var instances = [];

  function boot() {
    instances.forEach(function (instance) { instance.destroy(); });
    instances = [];

    Array.prototype.forEach.call(document.querySelectorAll('[data-field-guide]'), function (root) {
      instances.push(new FieldGuide(root));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Re-bind after a theme editor edit re-renders the section. */
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', boot);

  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target.closest('[data-field-guide]');

    if (root) {
      instances.forEach(function (instance) {
        if (instance.root === root) instance.revealBlock(event.target);
      });
      return;
    }

    /* A block in the outro section, which belongs to the reading view. */
    if (event.target.closest('[data-field-guide-results-only]')) {
      instances.forEach(function (instance) {
        if (instance.selected.length) instance.setView('results');
      });
    }
  });
})();
